import { BaseModel } from 'src/app/@shared/base.model';
import { FormMainType } from '@lib/types';

import { Cluster, Organization, Person, Topic } from '@common/entities';
import { OrganizationModel } from 'src/app/organization/@shared/organization.model';
import { PersonModel } from 'src/app/person/@shared/person.model';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { WhereFilter } from '@common/filter.decorator';

import { ContactPersonModel } from './contact-person.model';
import { toContactPersonListItem } from '@api/contact-person/@shared/contact-person-list-item.dto';

export type ClusterAddressMemberType = 'selfHelpAddress' | 'otherAddress';
export type ClusterMemberType = ClusterAddressMemberType | 'person' | 'contactPerson' | 'topic';

export type ClusterSelectionState<T> = {
  isAllSelected: boolean;
  excludedIds: string[];
  whereFilter?: WhereFilter<T>;
};

export class ClusterModel extends BaseModel<Cluster> {
  public type: FormMainType;
  public organizations: OrganizationModel[] = [];
  public persons: PersonModel[] = [];
  public contactPersons: ContactPersonModel[] = [];
  public topics: TopicModel[] = [];
  public isTopicCluster: boolean = false;
  public isSmartCluster: boolean = false;
  public whereFilterByType: Partial<Record<ClusterAddressMemberType, WhereFilter<Organization>>> & {
    person?: WhereFilter<Person>;
    contactPerson?: WhereFilter<Person>;
    topic?: WhereFilter<Topic>;
  };

  public includeByType: Partial<Record<ClusterAddressMemberType, boolean>> & {
    person?: boolean;
    contactPerson?: boolean;
    topic?: boolean;
  };

  public allSelectionByType?: Partial<Record<ClusterAddressMemberType, ClusterSelectionState<Organization>>> & {
    person?: ClusterSelectionState<Person>;
    contactPerson?: ClusterSelectionState<Person>;
    topic?: ClusterSelectionState<Topic>;
  };

  public isMixedCluster: boolean = false;
  public notes: string;

  // eslint-disable-next-line max-lines-per-function
  constructor(source: Cluster) {
    super(source);

    this.title = source.title;
    this.notes = source.notes;

    if (source.organizations?.length) {
      this.organizations = source.organizations
        .map((o) => {
          return new OrganizationModel(o);
        })
        .sort((a, b) => {
          if (a.displayName < b.displayName) {
            return -1;
          } else if (a.displayName === b.displayName) {
            return 0;
          }

          return 1;
        });
    }

    if (source.persons?.length) {
      this.persons = source.persons
        .map((p) => {
          return new PersonModel(p);
        })
        .sort((a, b) => {
          if (a.displayName < b.displayName) {
            return -1;
          } else if (a.displayName === b.displayName) {
            return 0;
          }

          return 1;
        });
    }
    this.contactPersons = (source.contactPersons || []).map((contact) => { return new ContactPersonModel(toContactPersonListItem(contact)); });
    this.topics = (source.topics || []).map((topic) => { return new TopicModel(topic); })
      .sort((left, right) => { return left.displayName.localeCompare(right.displayName); });
    this.isTopicCluster = !!source.isTopicCluster;
    this.type = 'cluster';

    this.whereFilterByType = source.whereFilterByType as any || {};
    this.allSelectionByType = source.allSelectionByType as any || {};
    this.isSmartCluster = typeof source.isSmartCluster === 'boolean'
      ? source.isSmartCluster
      : !!this.whereFilterByType?.selfHelpAddress
      || !!this.whereFilterByType?.otherAddress
      || !!this.whereFilterByType?.person;

    this.includeByType = this.isSmartCluster
      ? ClusterModel.getNormalizedIncludeByType(source.includeByType)
      : source.includeByType || {};

    this.isMixedCluster = !!source.isMixedCluster || (!!this.persons.length && !!this.organizations.length);
  }

  private static getNormalizedIncludeByType(includeByType: Cluster['includeByType'] = {}): ClusterModel['includeByType'] {
    return {
      contactPerson: includeByType.contactPerson === true,
      otherAddress: includeByType.otherAddress !== false,
      person: includeByType.person !== false,
      selfHelpAddress: includeByType.selfHelpAddress !== false,
      topic: includeByType.topic === true,
    };
  }
}
